import { Link, MarkdownRenderer, Text } from '@aurora-components';

import { useTranslations } from 'next-intl';
import { ImageComponent } from '../image/image';
import type { CloudinaryCropResize, CloudinaryGravity } from '../image/types';
import {
  CardWrapper,
  ContentWrapper,
  DescriptionWrapper,
  ImageWrapper,
  SimplePresentationCardBorder,
} from './card.styles';
import { CARD_TYPES } from './constants';
import type { BaseCard, ImageInterface } from './types';

interface CardProps {
  card_template: string;
  card: BaseCard;
}

const getTitleVariant = (card_template?: string) => {
  switch (card_template) {
    case CARD_TYPES.BASE_CARD:
      return 'h3';
    case CARD_TYPES.BLOG_POST_CARD:
    case CARD_TYPES.SIMPLE_PRESENTATION_CARD:
      return 'card';
    default:
      return 'h5';
  }
};

export function Card({ card_template, card }: CardProps) {
  const { imageId, alt_text, assetConnection, gravity, crop_resize, dimensions } =
    (card?.image as ImageInterface) || {};
  const t = useTranslations('common');

  return (
    <CardWrapper theme={card_template} $backgroundcolor={card.backgroundColor}>
      {card.image && (
        <ImageWrapper theme={card_template}>
          <ImageComponent
            alt={alt_text}
            imageId={imageId}
            imageSuffix={assetConnection.edges[0].node.content_type}
            gravity={gravity as CloudinaryGravity}
            crop_resize={crop_resize as CloudinaryCropResize}
            withAspectRatio={true}
            width={dimensions?.width}
            height={dimensions?.height}
          />
        </ImageWrapper>
      )}
      <ContentWrapper theme={card_template}>
        {!!card.showDate && card.date && (
          <Text $color="primary-base100" $fontWeight="Light" $variant="xsmall">
            {card.date}
          </Text>
        )}
        {card.display_title && (
          <Text
            $variant={getTitleVariant(card_template)}
            $color={
              card_template === CARD_TYPES.PERSON_WITH_IMAGE || card_template === CARD_TYPES.SIMPLE_PRESENTATION_CARD
                ? 'ui-textGray'
                : 'primary-base100'
            }
            $fontWeight={card_template === CARD_TYPES.BASE_CARD ? 'Medium' : 'SemiBold'}
            $noBalancer
          >
            {card.display_title}
          </Text>
        )}
        {card.text && (
          <DescriptionWrapper theme={card_template}>
            <MarkdownRenderer
              $variant="smallparag"
              $fontWeight="Light"
              $color={card_template === CARD_TYPES.SIMPLE_PRESENTATION_CARD ? 'ui-textGray' : 'primary-base100'}
              $truncateVertical={card_template !== CARD_TYPES.BASE_CARD}
              $noBalancer
              markdown={card.text}
            />
          </DescriptionWrapper>
        )}
        {card.link && (
          <Link
            title={card.linkLabel || t('read-more')}
            url={card.link.charAt(0) === '/' ? card.link : `/${card.link}`}
          />
        )}
        {card_template === CARD_TYPES.SIMPLE_PRESENTATION_CARD && <SimplePresentationCardBorder />}
      </ContentWrapper>
    </CardWrapper>
  );
}
